import { Suspense, lazy, type CSSProperties } from 'react'
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { LoaderRemover } from '@/components/loader-remover'
import { Toaster } from '@deepguide-ai/ui/sonner'
import GeneralError from '@/pages/errors/general-error'
import NotFoundError from '@/pages/errors/not-found-error'
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router'
import { AdminModeProvider } from '@/context/AdminModeContext'
import { isTrustCenterHostModePath } from '@/lib/env'

// import { TanStackRouterDevtools } from '@tanstack/router-devtools'

// RFC-203: On a Trust Center custom domain, the site root (`/`) renders the
// public Trust Center (resolved from the host) instead of the app. Lazy-loaded
// so first-party bundles are unaffected. Path routes like `/trust/access/:token`
// stay on the normal router (they are host-agnostic).
const TrustCenterHostEntry = lazy(
  () => import('@/pages/trust-center/public/TrustCenterHostEntry'),
)

const AIDevtools =
  import.meta.env.MODE === 'development'
    ? lazy(() =>
        import('@ai-sdk-tools/devtools').then((m) => ({
          default: m.AIDevtools,
        })),
      )
    : null

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundError,
  errorComponent: GeneralError,
})

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  // Custom-domain landing: only intercept the public host-mode path(s); every
  // other path (assets, /trust/access, /trust/resources) flows through the
  // normal router. Predicate is shared with the (protected) auth-guard bypass —
  // see `isTrustCenterHostModePath` for the invariant both sides must honor.
  const showCustomDomainLanding = isTrustCenterHostModePath(pathname)

  return (
    <NuqsAdapter>
        <AdminModeProvider>
          <Suspense
            fallback={
              <div className='flex h-screen w-screen items-center justify-center'>
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='black'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='animate-spin'
                >
                  <circle cx='12' cy='12' r='10' strokeOpacity='0.25' />
                  <path d='M12 2a10 10 0 0 1 10 10' />
                </svg>
              </div>
            }
          >
            {showCustomDomainLanding ? <TrustCenterHostEntry /> : <Outlet />}
            <LoaderRemover />
            <Toaster position='top-center' style={{ '--width': '560px' } as CSSProperties} />
            {AIDevtools && (
              <Suspense fallback={null}>
                <AIDevtools
                  config={{
                    streamCapture: {
                      enabled: true,
                      endpoint: '/api/ai/',
                      autoConnect: true,
                    },
                  }}
                />
              </Suspense>
            )}
            {/* {import.meta.env.MODE === 'development' && (
              <>
                <ReactQueryDevtools buttonPosition='bottom-left' />
                <TanStackRouterDevtools position='bottom-right' />
              </>
            )} */}
          </Suspense>
        </AdminModeProvider>
      </NuqsAdapter>
  )
}
